/** @format */

import { useState, useCallback } from 'react';
import * as campaignService from '../services/campaignsService';
import { CampaignFormInput } from '../types';

interface UseCampaignFormReturn {
  campaigns: CampaignFormInput[];
  loading: boolean;
  error: string | null;
  success: boolean;
  addCampaign: () => void;
  removeCampaign: (index: number) => void;
  updateCampaign: (index: number, campaign: Partial<CampaignFormInput>) => void;
  submitCampaigns: () => Promise<boolean>;
  resetForm: () => void;
  clearMessage: () => void;
}

/**
 * Custom hook for campaign form state management
 */
export const useCampaignForm = (): UseCampaignFormReturn => {
  const [campaigns, setCampaigns] = useState<CampaignFormInput[]>([
    {
      queueId: 0,
      vendorName: '',
      globalName: '',
      globalId: '',
      cost: undefined,
      buffer: undefined,
      officeId: '',
    },
  ]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const addCampaign = useCallback(() => {
    setCampaigns((prev) => [
      ...prev,
      {
        queueId: 0,
        vendorName: '',
        globalName: '',
        globalId: '',
        cost: undefined,
        buffer: undefined,
        officeId: '',
      },
    ]);
  }, []);

  const removeCampaign = useCallback((index: number) => {
    setCampaigns((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const updateCampaign = useCallback((index: number, campaign: Partial<CampaignFormInput>) => {
    setCampaigns((prev) => {
      const newCampaigns = [...prev];
      newCampaigns[index] = { ...newCampaigns[index], ...campaign };
      return newCampaigns;
    });
  }, []);

  const resetForm = useCallback(() => {
    setCampaigns([
      {
        queueId: 0,
        vendorName: '',
        globalName: '',
        globalId: '',
        cost: undefined,
        buffer: undefined,
        officeId: '',
      },
    ]);
    setError(null);
    setSuccess(false);
  }, []);

  const submitCampaigns = useCallback(async (): Promise<boolean> => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // Filter out campaigns with empty required fields
      const validCampaigns = campaigns.filter(
        (c) => c.queueId && c.vendorName.trim() !== ''
      );

      if (validCampaigns.length === 0) {
        setError('Please add at least one campaign with queueId and vendorName');
        setLoading(false);
        return false;
      }

      // Use batch endpoint even for single campaign
      await campaignService.batchCreateCampaigns(validCampaigns);
      setSuccess(true);
      resetForm();
      return true;
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error?.error ||
        'Failed to submit campaigns';
      setError(errorMessage);
      console.error('Error submitting campaigns:', err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [campaigns, resetForm]);

  const clearMessage = useCallback(() => {
    setError(null);
    setSuccess(false);
  }, []);

  return {
    campaigns,
    loading,
    error,
    success,
    addCampaign,
    removeCampaign,
    updateCampaign,
    submitCampaigns,
    resetForm,
    clearMessage,
  };
};
